/**
 * planner_approve_keywords — move keyword-feed candidates to approved.
 *
 * The best clicks-over-risk tool in the planner set: it replaces a per-row
 * click in a list an operator otherwise scrolls, and the row it writes never
 * reaches generated content, so the blast radius is a config column.
 *
 * Three gates run in `resolve`, before anything is written, so a batch never
 * half-lands:
 *   1. status must still be approvable,
 *   2. `duplicate_status` must be `clear` — the server's own gate, pre-checked
 *      so the agent gets one sentence instead of a mid-batch 409,
 *   3. a keyword-vetting `swap` verdict is REFUSED unless the caller passes
 *      `acknowledge_cannibalization: true`.
 *
 * Gate 3 exists because the human path guards this with a `window.confirm`
 * inside the page handler. No tool path may reach that — the browser blocks it
 * and page code cannot answer it — and on this branch the server does not gate
 * it either. So without this argument the agent path would be strictly weaker
 * than the human one and could approve cannibalizing keywords a person is
 * stopped from approving.
 */
import {
  defineCommand,
  refuse,
  isRefusal,
  renderPatchValue,
  type CommitAuthorization,
  type Diff,
  type ReadDeps,
  type Receipt,
  type Refusal,
  type ResolveInput,
  type TenantId,
  type WriteDeps,
} from '../contract';
import { dispatchAndWait } from '../handshake';
import {
  approveKeywordsArgs,
  gateCandidate,
  nextCalendarMonthBucket,
  projectApproval,
  type ApproveKeywordsArgs,
  type FeedCandidate,
} from './plannerApproveKeywordsCore';

const FEED_TABLE = 'keyword_feed_items';

/** Rows the gates cleared, plus the month bucket the diff and receipt quote. */
interface ApproveTarget {
  readonly rows: readonly FeedCandidate[];
  readonly nextMonthBucket: string;
}

function toCandidate(id: string, row: Readonly<Record<string, unknown>>): FeedCandidate {
  return {
    id,
    primary_keyword: String(row.primary_keyword ?? ''),
    status: String(row.status ?? ''),
    duplicate_status: String(row.duplicate_status ?? ''),
    hermes_verdict: row.hermes_verdict == null ? null : String(row.hermes_verdict),
    hermes_suggested_swap:
      row.hermes_suggested_swap == null ? null : String(row.hermes_suggested_swap),
    target_month: row.target_month == null ? null : String(row.target_month),
  };
}

async function loadById(
  read: ReadDeps,
  tenant: TenantId,
  ids: readonly string[],
): Promise<readonly FeedCandidate[] | Refusal> {
  const rows: FeedCandidate[] = [];
  for (const id of ids) {
    const row = await read.currentRow(FEED_TABLE, tenant, id);
    if (!row) {
      return refuse(`No keyword feed item ${id} for this tenant.`, { retryable: false });
    }
    rows.push(toCandidate(id, row));
  }
  return rows;
}

export const plannerApproveKeywords = defineCommand<ApproveKeywordsArgs, ApproveTarget>({
  name: 'planner_approve_keywords',
  section: 'feed',
  consequence: 'writes-config',
  description:
    'Approve keyword-feed candidates by id so they can be generated. Refuses duplicates and ' +
    'keywords flagged for cannibalization unless explicitly acknowledged. Approving also sets ' +
    'the target month when the row has none.',
  args: approveKeywordsArgs,

  async resolve(input: ResolveInput<ApproveKeywordsArgs>): Promise<ApproveTarget | Refusal> {
    const { args, tenant, read, signal } = input;
    if (signal.aborted) return refuse('Aborted before reading the feed.', { retryable: true });

    // Keyword-text resolution needs a feed list seam that does not exist yet:
    // ReadDeps.findArticles returns articles, not feed candidates, so it can
    // neither match on primary_keyword nor supply a candidate list. Refusing is
    // honest; silently approving a guessed id would not be.
    if (args.keywords?.length) {
      return refuse(
        'Approving by keyword text needs the feed read seam, which is not available yet. ' +
          'Pass explicit feed item ids instead.',
        { retryable: false },
      );
    }

    const ids = args.ids ?? [];
    const loaded = await loadById(read, tenant, ids);
    if (isRefusal(loaded)) return loaded;

    for (const row of loaded) {
      const blocked = gateCandidate(row, args.acknowledge_cannibalization);
      if (blocked) return blocked;
    }

    return { rows: loaded, nextMonthBucket: nextCalendarMonthBucket(new Date()) };
  },

  /**
   * Declares EXACTLY the patch the seam binding builds, because the mint digest
   * is `patchFromDiff(diff)` and the writer digest is over the binding's patch;
   * a field the two spell differently makes every write refuse.
   *
   * So the per-keyword detail lives in the receipt, not here. `ids` is sorted
   * for the same reason the binding sorts: two orderings of one set must digest
   * identically. The month auto-fill is called out in `reachesGeneratedContent`
   * terms below and in the summary, but it is NOT a change this tool sends —
   * the backend derives it (keyword_feed_service.py:671-677), so putting it in
   * the patch would claim a write we do not make.
   */
  diff(target: ApproveTarget, tenant: TenantId): Diff {
    const ids = target.rows.map((r) => r.id).sort();
    return {
      table: FEED_TABLE,
      rowId: ids.length === 1 ? ids[0] : null,
      tenant,
      changes: [
        {
          field: 'status',
          from: target.rows.length === 1 ? target.rows[0].status : null,
          to: renderPatchValue('approved'),
          authorability: 'exact' as const,
        },
        {
          field: 'ids',
          from: null,
          to: renderPatchValue(ids.join(',')),
          authorability: 'exact' as const,
        },
      ],
      // A feed row is planning state; it is not projected into generation
      // prompts. Approving only makes the keyword eligible to be generated.
      reachesGeneratedContent: false,
    };
  },

  async run(
    target: ApproveTarget,
    auth: CommitAuthorization,
    write: WriteDeps,
  ): Promise<Receipt | Refusal> {
    const approve = write.approveKeyword;
    if (!approve) {
      return refuse('This build has no keyword approval seam wired.', { retryable: false });
    }

    // One call, one authorization. The auth is consumed by the guarded seam on
    // first use, so looping per row would refuse every row after the first.
    const result = await approve.call(
      write,
      auth,
      target.rows.map((r) => r.id),
    );
    if (isRefusal(result)) return result;

    const approved = target.rows.map((r) => r.primary_keyword);

    // The view must confirm before the agent reads again, or it re-reads a feed
    // that still shows these rows as candidates and concludes the write failed.
    const settled = await dispatchAndWait('planner-keywords-approved', {
      ids: target.rows.map((r) => r.id),
    });

    const monthFilled = target.rows.filter((r) => !r.target_month).length;
    return {
      summary:
        `Approved ${approved.length} keyword${approved.length === 1 ? '' : 's'}: ${approved.join(', ')}.` +
        (monthFilled
          ? ` Target month set to ${target.nextMonthBucket} on ${monthFilled} of them.`
          : ''),
      facts: {
        approved: approved.length,
        target_month_filled: monthFilled,
        view_confirmed: settled.confirmed,
      },
    };
  },
});
